import React, { ReactNode, AriaAttributes } from 'react';
import {
  FloatingPortal,
  FloatingFocusManager,
  FloatingNode,
} from '@floating-ui/react';
import classNames from 'classnames';
import { usePopoverContext } from '../../Popover';
import { PopoverCloseButton } from '../PopoverCloseButton/PopoverCloseButton';
import { PopoverArrow } from '../PopoverArrow/PopoverArrow';
import * as styles from './PopoverContent.module.scss';

type LabelProps =
  | { 'aria-label': string; 'aria-labelledby'?: never }
  | { 'aria-labelledby': string; 'aria-label'?: never };

type PopoverContentProps = LabelProps &
  AriaAttributes & {
    className?: string;
    children: ReactNode;
    hideArrow?: boolean;
    id?: string;
    role?: string;
  };

export const PopoverContent = ({
  className,
  children,
  hideArrow = false,
  id,
  role = 'dialog',
  ...ariaProps
}: PopoverContentProps) => {
  const {
    renderCloseButton,
    refs,
    getFloatingProps,
    floatingStyles,
    context,
    nodeId,
  } = usePopoverContext();

  // If popover is not open then dont render any content
  if (!context.open) return null;

  return (
    <FloatingNode id={nodeId}>
      <FloatingPortal>
        <FloatingFocusManager
          context={context}
          modal={false}
          // Focus the first focusable element inside the popover
          order={['floating', 'content']}
          // Return focus to the trigger when closing
          returnFocus={true}
        >
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={classNames(styles.container, className)}
            {...getFloatingProps()}
            {...ariaProps}
            role={role}
            id={id}
            aria-modal="false"
          >
            <div
              className={classNames(styles.content, {
                [styles.hasRightPadding]: renderCloseButton,
              })}
            >
              {renderCloseButton && (
                <div className={styles.closeButton}>
                  <PopoverCloseButton />
                </div>
              )}
              {children}
            </div>
            {!hideArrow && <PopoverArrow />}
          </div>
        </FloatingFocusManager>
      </FloatingPortal>
    </FloatingNode>
  );
};
